import { getPlayableUrlWithToken } from "connection/mux";
import React, { createContext, useCallback, useEffect, useState } from "react";
import { SessionPlaybackVideo } from "types/working-model";
import { getSessionStorageObject, setSessionStorageObject } from "utils/session-storage";
import { jwtDecode } from "utils/utils";

interface VideoSecurityGateProps {
	playbackUrl: string | undefined | null;
	drmExpected: boolean | undefined;
	children: React.ReactNode | React.ReactNode[] | ((state: VideoSecurityGateState) => React.ReactNode | React.ReactNode[]);
	fallback?: React.ReactNode;
	playbackToken?: string;
	eventUuid?: string;
	policy?: SessionPlaybackVideo['policy'];
	/** keep rendering children while a new playback url is signed, rather than swapping to the fallback */
	renderWhileLoading?: boolean;
}

type VideoSecurityGateState = {
	url: string | undefined | null;
	drmToken: string | undefined;
	drmRequired: boolean | undefined;
	playbackId: string | undefined;
	loading: boolean;
}

const initialState: VideoSecurityGateState = {
	url: undefined,
	drmToken: undefined,
	drmRequired: undefined,
	playbackId: undefined,
	loading: true,
};

const MS_PER_HOUR = 60 * 60 * 1000;

// a cached signed url must not outlive its token, otherwise a reload hands the player an expired signature
// returns undefined when the url is unsigned (cache with no expiration) and 0 when the token has already expired
const getSignedUrlTtlInHours = (url: string | undefined | null): number | undefined => {
	try {
		const token = url ? new URL(url).searchParams.get('token') : null;
		if (!token) return undefined;

		const { exp } = jwtDecode<{ exp?: number }>(token) as { exp?: number };
		if (!exp) return undefined;

		return Math.max(exp * 1000 - Date.now(), 0) / MS_PER_HOUR;
	} catch {
		return undefined;
	}
};

export const VideoSecurityGateContext = createContext<VideoSecurityGateState>(initialState);

export default function VideoSecurityGate({ playbackUrl, drmExpected, children, fallback, playbackToken, eventUuid, policy, renderWhileLoading }: VideoSecurityGateProps) {
	const [state, setState] = useState<VideoSecurityGateState>(initialState);

	const loadTokens = useCallback(async () => {
		try {
			if (!playbackUrl) {
				setState({ ...initialState, loading: false });
				return;
			}

			const stored = getSessionStorageObject(`${playbackUrl}`);
			if (stored) {
				setState({ ...stored, loading: false });
				return;
			}

			if (playbackToken) {
				// if there is a token passed, optimistically allow passage so there's less load time for the player
				// if the token is bad, the response will still catch it and update the playback url to be the real one
				setState(prev => ({ ...prev, loading: false, url: playbackUrl + '?token=' + playbackToken, ok: true }));
			} else {
				setState(prev => ({ ...prev, loading: true }));
			}

			const response = await getPlayableUrlWithToken(playbackUrl, drmExpected, playbackToken, eventUuid, policy);

			setState({
				...response,
				loading: false
			});

			const ttl = getSignedUrlTtlInHours(response.url);
			if (response.ok && ttl !== 0) {
				setSessionStorageObject(`${playbackUrl}`, response, ttl);
			}
		} catch (e) {
			console.error("Error loading video player tokens:", e);
			setState(initialState);
		} finally {
			setState(prev => ({ ...prev, loading: false }));
		}
	}, [playbackUrl, drmExpected, playbackToken, eventUuid, policy]);

	useEffect(() => {
		loadTokens();
	}, [loadTokens]);

	const content = () => {
		if (state.loading && !renderWhileLoading) {
			return fallback || null;
		}

		if (typeof children === "function") {
			return children(state);
		}

		return children;
	};

	return (
		<VideoSecurityGateContext.Provider value={state}>
			{content()}
		</VideoSecurityGateContext.Provider>
	);
}
